// Helper to determine if input has context (clef represented by colon)
export const hasContext = (input: string): boolean => {
  const parts = input.split(":");
  return parts.length > 1 && parts[0].trim().length > 0;
  return input.includes(":");
};

export const parseNoteToMusicXML = (input: string): string => {
  let contextPart = "treble:C";
  let contentPart = input;

  if (input.includes(":")) {
    const parts = input.split(":");
    contextPart = parts[0];
    contentPart = parts[1];
  } else {
    // Default context if none provided (Explicit Requirement: Always show Clef/Key)
    // We treat "No Context" as "Default Context"
    contextPart = "treble:C";
  }

  // Parse Context
  // Regex: ^(clef)?(key)?$
  // Hard to regex strictly because key can be "Am".
  // Simple heuristic:
  // Clefs: treble, bass, alto, tenor
  // Keys: A-G, #, b, m

  let clef = "treble";
  let key = "C";

  const lowerContext = contextPart.toLowerCase();

  if (lowerContext.includes("bass")) clef = "bass";
  else if (lowerContext.includes("alto")) clef = "alto";
  else if (lowerContext.includes("tenor")) clef = "tenor";
  else clef = "treble";

  // Key is whatever is left? Or just parse strictly.
  // Actually, usually users type "bassC" or "trebleAm".
  // Remove clef string from context to get key.
  let keyStr = lowerContext.replace(clef, "").trim();
  if (keyStr.length > 0) {
    // Restore casing might be needed for logic?
    // Logic uses lowercase for map, but let's be careful.
    // The current logic calculates fifths from a map.
    key = keyStr; // defaulting to whatever they typed
  } else {
    // If they typed "bass:", key is C major.
    key = "C";
  }

  const fifths = calculateFifths(key);

  return generateMusicXML(clef, fifths, contentPart);
};

export const estimateWidth = (input: string): number => {
  // Base width for Clef (30) + Key (depends) + Time (0)
  // Precise width estimation to ensure "Tight" fit (centering the content)
  let base = 70; // Clef (30) + Margins (40)

  const perAccidental = 15; // Width of a Sharp/Flat
  const perNote = 40; // Width of a Note

  let fifths = 0;

  // Quick parse to get fifths (simplified)
  if (input.includes(":")) {
    const parts = input.split(":");
    const contextPart = parts[0].toLowerCase();

    let clef = "treble";
    if (contextPart.includes("bass")) clef = "bass";
    else if (contextPart.includes("alto")) clef = "alto";
    else if (contextPart.includes("tenor")) clef = "tenor";

    const keyStr = contextPart.replace(clef, "").trim() || "C";
    fifths = calculateFifths(keyStr);
  }

  // Note count
  const parts = input.split(":");
  const notesStr = parts.length > 1 ? parts[1] : parts[0];
  const noteCount =
    notesStr.split(" ").filter((s) => s.trim().length > 0).length;

  // Calculate total layout width needed
  const calculated = base + (Math.abs(fifths) * perAccidental) +
    (Math.max(1, noteCount) * perNote);

  // Min width 220 (Was 260) - tightening it up since we are reducing padding
  return Math.max(220, calculated);
};

const calculateFifths = (keyStr: string): number => {
  const k = keyStr.toLowerCase();
  const isMinor = k.endsWith("m");
  const root = isMinor ? k.slice(0, -1) : k;

  const circleOfFifths: Record<string, number> = {
    "c": 0,
    "g": 1,
    "d": 2,
    "a": 3,
    "e": 4,
    "b": 5,
    "f#": 6,
    "c#": 7,
    "f": -1,
    "bb": -2,
    "eb": -3,
    "ab": -4,
    "db": -5,
    "gb": -6,
    "cb": -7,
  };

  let val = circleOfFifths[root];
  if (val === undefined) val = 0; // Default C

  if (isMinor) {
    val -= 3;
  }

  return val;
};

const getDuration = (d?: string): { duration: number; type: string } => {
  if (!d) return { duration: 24, type: "quarter" };
  if (d === "w") return { duration: 96, type: "whole" };
  if (d === "h") return { duration: 48, type: "half" };
  if (d === "q") return { duration: 24, type: "quarter" };
  if (d === "8") return { duration: 12, type: "eighth" };
  if (d === "16") return { duration: 6, type: "16th" };
  return { duration: 24, type: "quarter" };
};

const generateMusicXML = (
  clef: string,
  fifths: number,
  notePart: string,
): string => {
  const clefMap: Record<string, { sign: string; line: number }> = {
    "treble": { sign: "G", line: 2 },
    "bass": { sign: "F", line: 4 },
    "alto": { sign: "C", line: 3 },
    "tenor": { sign: "C", line: 4 },
  };
  const c = clefMap[clef.toLowerCase()] || clefMap["treble"];

  const notes = notePart.split(" ").filter((s) => s.trim().length > 0);

  // Centering Hack:
  // User wants the note "Centered" on the staff card.
  // Standard MusicXML aligns left (Clef -> Key -> Note).
  // To center it in a fixed-width container, we inject hidden padding (Invisible Rest).
  // Target Width: ~220px. Center: ~110px.
  // Clef (~30) + Key (~20?) + Padding (~40) -> Note Start (~90-100).
  const paddingBuffer =
    `<note print-object="no"><rest/><duration>24</duration><voice>1</voice><type>quarter</type></note>`;

  const notesXML = notes.map((noteStr) => {
    // Parse: (step)(accidental)?(octave)(/duration)?
    const match = noteStr.match(/^([A-Ga-g])([#b])?(\d+)(?:\/(\w+))?$/);
    if (!match) return "";

    const step = match[1].toUpperCase();
    const accidental = match[2] || "";
    const octave = match[3];
    const durationPart = match[4];

    const durInfo = getDuration(durationPart);

    // Accidental logic
    let alterTag = "";
    let accidentalTag = "";

    if (accidental) {
      const isSharp = accidental === "#";
      const alterVal = isSharp ? 1 : -1;

      if (!isAccidentalInKey(step, accidental, fifths)) {
        alterTag = `<alter>${alterVal}</alter>`;
        accidentalTag = `<accidental>${
          isSharp ? "sharp" : "flat"
        }</accidental>`;
      } else {
        // It's in key, so we need alter tag for pitch but NO accidental display
        alterTag = `<alter>${alterVal}</alter>`;
      }
    } else {
      // Natural? If key has sharp/flat for this step, we might need natural?
      // For now assume explicit natural if user wanted? but input doesn't support 'n' yet.
      // Ignored for simplicty.
    }

    return `<note>
        <pitch>
          <step>${step}</step>
          ${alterTag}
          <octave>${octave}</octave>
        </pitch>
        <duration>${durInfo.duration}</duration>
        <type>${durInfo.type}</type>
        ${accidentalTag}
      </note>`;
  }).join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Music</part-name>
    </score-part>
  </part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>24</divisions>
        <key>
          <fifths>${fifths}</fifths>
        </key>
        <clef>
          <sign>${c.sign}</sign>
          <line>${c.line}</line>
        </clef>
      </attributes>
${paddingBuffer}
${notesXML}
      <barline location="right">
        <bar-style>none</bar-style>
      </barline>
    </measure>
  </part>
</score-partwise>`;
};

const isAccidentalInKey = (
  step: string,
  accidental: string,
  fifths: number,
): boolean => {
  if (fifths === 0) return false;
  const sharpsOrder = ["F", "C", "G", "D", "A", "E", "B"];
  const flatsOrder = ["B", "E", "A", "D", "G", "C", "F"];

  if (fifths > 0) {
    if (accidental !== "#") return false;
    const sharpNotes = sharpsOrder.slice(0, fifths);
    return sharpNotes.includes(step);
  } else {
    if (accidental !== "b") return false;
    const flatNotes = flatsOrder.slice(0, Math.abs(fifths));
    return flatNotes.includes(step);
  }
};
